import { useTranslation } from "react-i18next";
import { Badge, Card, CardContent, CardHeader, CardTitle } from "~/components/ui";
import type { RecordUpdatePreview as RecordUpdatePreviewData } from "~/features/dataImportProjects/hooks/useRecordUpdatePreview";

type BadgeTone = React.ComponentProps<typeof Badge>["tone"];

// Joined source/target endpoint names for a relation row, shown under its record id so an operator
// reads "織田信長 → 賤ヶ岳の戦い" instead of a bare uuid. Renders nothing when neither endpoint is named.
function EndpointNames({ sourceLabel, targetLabel }: { sourceLabel?: string | null; targetLabel?: string | null }) {
  const { t } = useTranslation();
  if (!sourceLabel && !targetLabel) return null;
  return (
    <div className="text-12-regular text-fg-2">
      {t("feature.recordUpdate.records.endpoints", { source: sourceLabel ?? "?", target: targetLabel ?? "?" })}
    </div>
  );
}

// toIdentify BLOCKS proceed since #7203 (the backend refuses records missing
// their evidence-lineage identity) — render it as an error, not as progress.
const CLASSIFICATION_TONE: Record<string, BadgeTone> = {
  alreadyIdentified: "status-approved",
  toIdentify: "score-c",
  conflict: "score-c",
};

const CLASSIFICATION_LABEL_KEY: Record<string, string> = {
  alreadyIdentified: "feature.recordUpdate.classification.alreadyIdentified",
  toIdentify: "feature.recordUpdate.classification.toIdentify",
  conflict: "feature.recordUpdate.classification.conflict",
};

// Backend RecordUpdateUnresolvableReason enum → i18n keys, so an unresolvable row reads as a human
// label + one-line explanation (remedy folded in) instead of a raw enum. Kept in this file (like
// CLASSIFICATION_LABEL_KEY) so i18next-extract resolves the keys statically. Keep in sync when the
// backend adds a reason; unknown reasons fall back to the raw string.
const REASON_LABEL_KEY: Record<string, string> = {
  "no-committed-source-evidence": "feature.recordUpdate.unresolvable.reasons.noCommittedSourceEvidence.label",
  "unresolvable-identity-category": "feature.recordUpdate.unresolvable.reasons.unresolvableIdentityCategory.label",
  "multiple-creating-composers": "feature.recordUpdate.unresolvable.reasons.multipleCreatingComposers.label",
  "malformed-stored-identity": "feature.recordUpdate.unresolvable.reasons.malformedStoredIdentity.label",
  "relation-recompute-deferred": "feature.recordUpdate.unresolvable.reasons.relationRecomputeDeferred.label",
  "ambiguous-variant-key": "feature.recordUpdate.unresolvable.reasons.ambiguousVariantKey.label",
  "origin-composer-missing": "feature.recordUpdate.unresolvable.reasons.originComposerMissing.label",
  "origin-composer-not-replicable": "feature.recordUpdate.unresolvable.reasons.originComposerNotReplicable.label",
  "dispatch-identity-conflict": "feature.recordUpdate.unresolvable.reasons.dispatchIdentityConflict.label",
  "toidentify-variant-key-deferred": "feature.recordUpdate.unresolvable.reasons.toidentifyVariantKeyDeferred.label",
};

const REASON_DESCRIPTION_KEY: Record<string, string> = {
  "no-committed-source-evidence": "feature.recordUpdate.unresolvable.reasons.noCommittedSourceEvidence.description",
  "unresolvable-identity-category":
    "feature.recordUpdate.unresolvable.reasons.unresolvableIdentityCategory.description",
  "multiple-creating-composers": "feature.recordUpdate.unresolvable.reasons.multipleCreatingComposers.description",
  "malformed-stored-identity": "feature.recordUpdate.unresolvable.reasons.malformedStoredIdentity.description",
  "relation-recompute-deferred": "feature.recordUpdate.unresolvable.reasons.relationRecomputeDeferred.description",
  "ambiguous-variant-key": "feature.recordUpdate.unresolvable.reasons.ambiguousVariantKey.description",
  "origin-composer-missing": "feature.recordUpdate.unresolvable.reasons.originComposerMissing.description",
  "origin-composer-not-replicable": "feature.recordUpdate.unresolvable.reasons.originComposerNotReplicable.description",
  "dispatch-identity-conflict": "feature.recordUpdate.unresolvable.reasons.dispatchIdentityConflict.description",
  "toidentify-variant-key-deferred":
    "feature.recordUpdate.unresolvable.reasons.toidentifyVariantKeyDeferred.description",
};

function CountStat({ label, value, tone }: { label: string; value: number; tone: BadgeTone }) {
  return (
    <div className="flex flex-col items-start gap-1 rounded-[var(--radius-md)] border border-border-default p-3">
      <Badge tone={tone}>{label}</Badge>
      <span className="text-16-bold text-fg-1">{value}</span>
    </div>
  );
}

/**
 * Presentational blast-radius preview: the selected records plus the eligible
 * siblings that will be recomposed, each with its classification, the aggregate
 * counts, unresolvable records, and the manual-edit warning. Renders nothing
 * interactive — the sibling-confirmation gate and proceed action live in the
 * container.
 */
export function RecordUpdatePreview({ preview }: { preview: RecordUpdatePreviewData }) {
  const { t } = useTranslation();
  const { counts, records, unresolvable, warnings } = preview;
  // Only surface records that need attention: siblings (selected === false) that will be
  // recomposed and require acknowledgement, and toIdentify/conflict rows. A pasted record that
  // already resolved to its identity is fine, so it is omitted — the counts above still report it.
  const affectedRows = records.filter((r) => !(r.selected && r.classification === "alreadyIdentified"));

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <CountStat
          label={t("feature.recordUpdate.counts.alreadyIdentified")}
          value={counts.alreadyIdentified}
          tone="status-approved"
        />
        <CountStat label={t("feature.recordUpdate.counts.toIdentify")} value={counts.toIdentify} tone="score-c" />
        <CountStat label={t("feature.recordUpdate.counts.conflict")} value={counts.conflict} tone="score-c" />
        <CountStat label={t("feature.recordUpdate.counts.unresolvable")} value={counts.unresolvable} tone="default" />
      </div>

      {affectedRows.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>{t("feature.recordUpdate.records.title")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="max-h-72 overflow-auto">
              <table className="w-full text-12-regular">
                <thead>
                  <tr className="border-b border-border-default text-left text-fg-2">
                    <th className="p-2">{t("feature.recordUpdate.records.recordId")}</th>
                    <th className="p-2">{t("feature.recordUpdate.records.kind")}</th>
                    <th className="p-2">{t("feature.recordUpdate.records.role")}</th>
                    <th className="p-2">{t("feature.recordUpdate.records.classification")}</th>
                    <th className="p-2">{t("feature.recordUpdate.records.variantKey")}</th>
                    <th className="p-2">{t("feature.recordUpdate.records.identity")}</th>
                  </tr>
                </thead>
                <tbody>
                  {affectedRows.map((r) => (
                    <tr key={r.recordId} className="border-b border-border-default">
                      <td className="p-2 font-mono text-fg-3">
                        <div>{r.recordId}</div>
                        {r.kind === "relation" ? (
                          <EndpointNames sourceLabel={r.sourceLabel} targetLabel={r.targetLabel} />
                        ) : null}
                      </td>
                      <td className="p-2">
                        <Badge tone={r.kind === "entity" ? "primary" : "status-review"}>{r.kind}</Badge>
                      </td>
                      <td className="p-2">
                        <Badge tone={r.selected ? "primary" : "default"}>
                          {r.selected
                            ? t("feature.recordUpdate.records.selected")
                            : t("feature.recordUpdate.records.sibling")}
                        </Badge>
                      </td>
                      <td className="p-2">
                        <Badge tone={CLASSIFICATION_TONE[r.classification] ?? "default"}>
                          {CLASSIFICATION_LABEL_KEY[r.classification]
                            ? t(CLASSIFICATION_LABEL_KEY[r.classification])
                            : r.classification}
                        </Badge>
                      </td>
                      <td className="p-2 font-mono text-fg-3">{r.variantKey}</td>
                      <td className="p-2 font-mono text-fg-3">{r.identity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {warnings.newestCommitIsManual.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>{t("feature.recordUpdate.warnings.newestCommitIsManual")}</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="list-disc pl-5 font-mono text-12-regular text-fg-3">
              {warnings.newestCommitIsManual.map((id) => (
                <li key={id}>{id}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : null}

      {unresolvable.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>{t("feature.recordUpdate.unresolvable.title")}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-3 text-12-regular text-fg-3">{t("feature.recordUpdate.unresolvable.description")}</p>
            <div className="max-h-72 overflow-auto">
              <table className="w-full text-12-regular">
                <thead>
                  <tr className="border-b border-border-default text-left text-fg-2">
                    <th className="p-2">{t("feature.recordUpdate.records.recordId")}</th>
                    <th className="p-2">{t("feature.recordUpdate.unresolvable.reason")}</th>
                    <th className="p-2">{t("feature.recordUpdate.unresolvable.detail")}</th>
                  </tr>
                </thead>
                <tbody>
                  {unresolvable.map((u) => (
                    <tr key={u.recordId} className="border-b border-border-default">
                      <td className="p-2 font-mono text-fg-3">
                        <div>{u.recordId}</div>
                        <EndpointNames sourceLabel={u.sourceLabel} targetLabel={u.targetLabel} />
                      </td>
                      <td className="p-2">
                        <div className="text-fg-1">
                          {REASON_LABEL_KEY[u.reason] ? t(REASON_LABEL_KEY[u.reason]) : u.reason}
                        </div>
                        {REASON_DESCRIPTION_KEY[u.reason] ? (
                          <div className="text-12-regular text-fg-3">
                            {t(REASON_DESCRIPTION_KEY[u.reason], { detail: u.detail ?? "" })}
                          </div>
                        ) : null}
                      </td>
                      <td className="p-2 text-fg-3">{u.detail ?? "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
